import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { take } from 'rxjs/operators';
import { ClientService } from 'src/app/services/client.service';
import { ClientInfo } from '../../model/clientInfo.model';
import { ClientEditorComponent } from '../client-editor/client-editor.component';
import { NotesViewerComponent } from '../notes-viewer/notes-viewer.component';
import * as dayjs from 'dayjs';
import { DatePipe } from '@angular/common';
import { DxPopoverComponent } from 'devextreme-angular';
import { alert, custom } from 'devextreme/ui/dialog';
import { Client } from '../../model/client.model';
import { ConstantsService } from 'src/app/services/constants.service';
import { SessionContextService } from 'src/app/services/session-context.service';

@Component({
  selector: 'app-client-info-panel',
  templateUrl: './client-info-panel.component.html',
  styleUrls: ['./client-info-panel.component.css']
})
export class ClientInfoPanelComponent {
  @ViewChild(NotesViewerComponent) dlgNotes: NotesViewerComponent;
  @ViewChild(ClientEditorComponent) dlgClient: ClientEditorComponent;
  @ViewChild(DxPopoverComponent) dxPopover: DxPopoverComponent;

  public clientInfo: ClientInfo = null;
  public age: number;
  public dobYear: string;
  public dobDayAndMonth: string;
  public isBirthday = false;
  clientId = -1;
  clientPhotoUrl: string = '';
  datePipe: DatePipe = new DatePipe('en-GB');
  popoverShowing: boolean = false;
  popoverHovering: boolean = false;

  @Input() showClientInfo: boolean = true;
  @Input() showEditClientBtn: boolean = true;
  @Input() set ClientId(val) {
    this.setClientId(val);
  }

  @Output() onClientEdited: EventEmitter<number> = new EventEmitter<number>();
  @Output() onGetClientInfo: EventEmitter<ClientInfo> = new EventEmitter<ClientInfo>();

  constructor(private clientService: ClientService, private changeDetectorRef: ChangeDetectorRef, public session: SessionContextService) { }

  setClientId(val: number | -1) {
    if ((!this.clientInfo) || (!this.clientId) || (this.clientId!==val)){
      this.clientId = val;
      this.clientInfo = null;
      if (this.clientId > 0) {
        this.clientService.GetClientInfo(this.clientId).pipe(take(1)).subscribe((d) => { 
          const today = new Date();
          const dob = new Date(d.DateOfBirth);
          if (dob.getDate() == today.getDate() && dob.getMonth() == today.getMonth()) {
            this.isBirthday = true;
            this.dobDayAndMonth = this.datePipe.transform(d.DateOfBirth, 'E d MMM');
            this.dobYear = dob.getFullYear().toString();
          } else {
            this.isBirthday = false;
          }

          this.clientInfo = d; 
          this.onGetClientInfo.next(d); 
          this.age =
            d.DateOfBirth &&
            Math.floor(dayjs(new Date()).diff(d.DateOfBirth, 'year'));
          var cacheBuster = Math.round(new Date().getTime() / 1000);       
          this.clientPhotoUrl = d.ClientPhotoUrl+'&S=140&time='+ cacheBuster;

          // both showOnlineClientPopup and ShowIDCheckedPopup may make changes to Client object
          // so wait for one to complete before calling the other...
          if (this.clientInfo.NewOnlineClientWarningConfirmed === false) {
            alert('This client has registered online, please check their details', 'New Online Client').then(() => {
              this.clientInfo.NewOnlineClientWarningConfirmed = true;
              this.clientService.GetClient(this.clientId).subscribe((client: Client) => {
                this.clientService.CreateOrUpdateClient(client, 1).pipe(take(1)).subscribe((_res) => {
                  this.showIDCheckedPopup();
                })
              });
            });
          } else {
            this.showIDCheckedPopup();
          }
          this.showUrgentNotesPopup();
          this.changeDetectorRef.detectChanges();
        });
      }
    }
  }

  showIDCheckedPopup() {
    this.clientService.GetClient(this.clientId).subscribe((client: Client) => {
      if ((!client.IdChecked) && this.session.defaultData.defData.customerSettings.ClientRequireIDCheck){
        custom({
          title: 'New Online Client',
          messageHtml: 'Please check this client\'s ID',
          buttons: [{
            text: 'ID Checked',
            onClick: (e) => this.setIDChecked(),
          },
          {
            text: 'Cancel',
            onClick: (e) => false,
          }, ]
        }).show();
      }
    });
  }

  setIDChecked() {
    this.clientService.GetClient(this.clientId).subscribe((client: Client) => {
      client.IdChecked = true;
      client.IdCheckedDateTime = ConstantsService.MakeDateTimeGMT(new Date());
      this.clientService.CreateOrUpdateClient(client).pipe(take(1)).subscribe();
    });
  }

  btnShowClientDetails(e) {
    this.dlgClient.showClientById(this.clientInfo.Id, this.clientInfo.NewOnlineClientWarningConfirmed);
  }

  btnShowUrgentNotes(e) {
    this.dlgNotes.show('Urgent Notes', this.clientInfo, this.clientInfo.UrgentNotes);
  }

  btnShowNotes(e) {
    this.dlgNotes.show('Notes', this.clientInfo, this.clientInfo.Notes);
  }

  OnCloseDlg(e) {
    if (e) {
      this.setClientId(e.Id);
      
      // Make sure photo is updated if changed in client editor
      var cacheBuster = Math.round(new Date().getTime() / 1000);       
      this.clientPhotoUrl = this.clientPhotoUrl+'&time2='+ cacheBuster;
      
      this.onClientEdited.next(e.Id);
    }
  }

  HasNotes() {
    return this.clientInfo?.Notes?.length > 0 ? 'check' : null;
  }

  HasUrgentNotes() {
    return this.clientInfo?.UrgentNotes?.length > 0 ? 'check' : null;
  }

  get creditSystemName() {
    return this.clientInfo?.CreditList?.length > 0
      ? ' - ' +
          (this.clientInfo?.CreditList?.slice(1)?.findIndex(
            (i) => i.CreditSystem !== this.clientInfo?.CreditList[0]?.CreditSystem
          ) > -1
            ? 'Multiple Systems'
            : this.clientInfo.CreditList[0].CreditSystemName)
      : '';
  }

  get creditSystemBreakdown() {
    if (this.clientInfo && this.clientInfo.CreditList && this.clientInfo.CreditList.length > 0) {
      const result = [];
      this.clientInfo.CreditList.forEach((i) => {
        result.push(i.CreditsRemaining + ' - ' + i.CreditSystemName);
      });
      return result.join('<br/>');
    }
    return '';
  }

  get lastSessionDetails() {
    let details = '';
    if (this.clientInfo) {
      details = (this.clientInfo.LastSessionDesc || '') + ', ' + (this.clientInfo.LastSessionResource || '');
    }
    return details.trim().replace(/^,|,$/g, '');
  }

  onPopoverShowing(_e: any): void {
    window.addEventListener('mousemove', () => {
      let popoverFound = false;
      let hoveredElements = document.querySelectorAll(':hover');
      let hoveredElementsLength = hoveredElements.length;
      for (let i = 0; i < hoveredElementsLength; i++) {
        if (hoveredElements[i].id === 'creditInfoIconContainer' || hoveredElements[i].id === 'creditInfoPopover') {
          popoverFound = true;
          break;
        }
      }

      if (!popoverFound) {
        this.dxPopover.instance.hide();
      }
    });
  }

  onPopoverHiding(_e: any): void {
    window.removeAllListeners('mousemove');
  }

  showUrgentNotesPopup() {
    if (
      this.clientId > 0 &&
      !!this.showClientInfo &&
      this.clientInfo &&
      this.clientInfo?.UrgentNotes?.trim()?.length > 0
    ) {
      this.dlgNotes.show('Urgent Notes', this.clientInfo, this.clientInfo.UrgentNotes);
    }
  }
}
